import React, { useState } from "react";
import { Popconfirm, Card, Button, Divider, Space, Alert, notification } from "antd";
import useComplianceReport from "@Components/account/useComplianceReport";
import AccountService from "@Services/AccountService";
import ComplianceService from "@Services/ComplianceService";
import SessionManager from "@Core/session";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router";
import { PDFViewer } from "@react-pdf/renderer";
import TermsModal from "@Components/shared/TermsModal";
import { TermsTypes, TermsTitles, TermsConfig, SystemContext } from "@Constants";
import { useCreateTermsAction } from "@Components/shared/mutations";
import EditTermsModal from "@Components/shared/EditTermsModal";
import { EditOutlined } from "@mui/icons-material";
import { hasCapability } from "@Utils";
import { useGetLatestTerms, useGetUserAcceptedTerms, useGetAllTerms } from "@Components/shared/queries";

const AccountPrivacyTab: React.FC = () => {
    const { Report, downloadReport, previewReport, reportAction, requestReport, requestReportLoading, requestStatus } =
        useComplianceReport();
    const [deactivating, setDeactivating] = useState<boolean>(false);
    const [requestingDelete, setRequestingDelete] = useState<boolean>(false);
    const [isShowingReport, setIsShowingReport] = useState(false);
    const isPending = requestStatus === "pending";
    const queryClient = useQueryClient();
    const history = useHistory();
    const [isTermsModalVisible, setTermsModalVisible] = useState(false);
    const [termsContent, setTermsContent] = useState("");
    const [currentTermsType, setCurrentTermsType] = useState("");
    const { data: termsData, isLoading: loadingTerms } = useGetLatestTerms(TermsTypes.Terms);
    const { data: privacyData, isLoading: loadingPrivacy } = useGetLatestTerms(TermsTypes.PrivacyPolicy);
    //Get all latest types of terms that the logged in user has accepted
    const { data: userAcceptedTerms, isLoading: loadingUserAcceptedTerms } = useGetUserAcceptedTerms("", true);
    const { mutate: createTermsAction, isLoading: isCreatingTermsAction } = useCreateTermsAction();
    const [isEditorModalVisible, setEditorModalVisible] = useState(false);
    const roleGroups = SessionManager.getServiceUser()?.roleGroups ?? [];

    // Get all terms for the dropdown
    const { data: allTermsData, isLoading: loadingAllTerms } = useGetAllTerms();

    // Use the terms array from the response, or empty array if not available
    const termsList = allTermsData?.terms ? allTermsData.terms : [];

    // Map the data and loading states to each term type
    const termsQueries = {
        [TermsTypes.Terms]: { data: termsData, isLoading: loadingTerms },
        [TermsTypes.PrivacyPolicy]: { data: privacyData, isLoading: loadingPrivacy },
    };

    const hasCurrentTermBeenAccepted = userAcceptedTerms?.userAcceptedTerms?.some(
        (term) => term.termsType === currentTermsType && term.acceptedOn != null
    );

    /**
     * Create a deletion request.
     */
    const deactivate = async () => {
        setDeactivating(true);
        const service = new AccountService();
        const result = await service.deactivate();
        setDeactivating(false);
        if (!result.hasErrors) {
            await service.logout(); // ? REVIEW: should we check for errors here?
            queryClient.clear();
            history.push("/login");
            notification.success({
                message: "Account deactivated.",
            });
        } else {
            notification.error({
                message: "There was an error deactivating your account. Please contact us.",
            });
        }
    };

    /**
     * Create a deletion request.
     */
    const requestDeletion = async () => {
        setRequestingDelete(true);
        const service = new ComplianceService();
        const result = await service.create(SessionManager.getServiceUser()?.userId, "deletion");
        setRequestingDelete(false);
        if (!result.hasErrors) {
            const accountService = new AccountService();
            await accountService.logout();
            queryClient.clear();
            history.push("/login");
            notification.success({
                message: "Account deletion requested.",
            });
        } else {
            notification.error({
                message: "There was an error creating your request. Please contact us.",
            });
        }
    };

    const requestsActive = requestReportLoading || !!reportAction || requestingDelete || deactivating;

    const handleTermsClick = (type: string) => {
        setCurrentTermsType(type);
        const termData = termsQueries[type]?.data;
        if (termData) {
            setTermsContent(termData.content);
            setTermsModalVisible(true);
        }
    };

    const handleAgreeTerms = (termsVersionId: string) => {
        createTermsAction({ termsVersionId, accepted: true });
    };

    const handleEditTerms = () => {
        setEditorModalVisible(true);
    };

    return (
        <Card title="Account Privacy" className="newUI-raised">
            <div className="flex flex-justify-space-between">
                <h4 className="card-subtitle mb-2 text-muted subheading">Terms and Conditions</h4>
                {roleGroups.includes("TermsEditor") ? (
                    <Button onClick={handleEditTerms} style={{ backgroundColor: "transparent", border: "none" }}>
                        <EditOutlined />
                    </Button>
                ) : null}
            </div>
            <p>Review our latest terms and conditions and privacy policy.</p>
            <Space wrap>
                {TermsConfig.map((config) => (
                    <Button
                        key={config.type}
                        className="secondary"
                        onClick={() => handleTermsClick(config.type)}
                        loading={termsQueries[config.type]?.isLoading || loadingUserAcceptedTerms}
                    >
                        {config.label}
                    </Button>
                ))}
            </Space>
            <Divider />
            <h4 className="card-subtitle mb-2 text-muted subheading">Deactivate Account</h4>
            <p>
                This action will make your account inactive and you will be instantly logged out. Once deactivated,
                other users will no longer be able to find or view your profile.
            </p>
            <Alert
                description="You can reactivate your account at any time simply by signing back into the platform."
                type="info"
                showIcon
            />
            <Popconfirm
                placement="top"
                title="Deactivate your account?"
                okText="Yes, deactivate my account"
                cancelText="Cancel"
                className="confirmation"
                onConfirm={deactivate}
                cancelButtonProps={{ className: "secondary" }}
            >
                <Button
                    className="secondary danger"
                    style={{ marginTop: "20px" }}
                    loading={deactivating}
                    disabled={requestsActive}
                >
                    Deactivate account
                </Button>
            </Popconfirm>
            <Divider />
            <h4 className="card-subtitle mb-2 text-muted subheading">Data Access</h4>
            <p>Request a copy of all of the data we keep about you.</p>
            <Space wrap>
                {requestStatus === "completed" && (
                    <>
                        <Button
                            className="secondary"
                            disabled={requestsActive}
                            loading={reportAction === "preview"}
                            onClick={async () => {
                                isShowingReport
                                    ? setIsShowingReport(false)
                                    : await previewReport().then(() => setIsShowingReport(true));
                            }}
                        >
                            {!isShowingReport ? "Preview report" : "Close preview"}
                        </Button>
                        <Button
                            className="secondary"
                            disabled={requestsActive || !downloadReport}
                            loading={reportAction === "download"}
                            onClick={downloadReport}
                        >
                            Download report
                        </Button>
                    </>
                )}
                <Button
                    className="secondary"
                    disabled={isPending || requestsActive}
                    loading={requestReportLoading}
                    onClick={async () => {
                        setIsShowingReport(false);
                        await requestReport();
                    }}
                >
                    {isPending ? "Data requested" : "Request data"}
                </Button>
            </Space>
            {isPending && (
                <>
                    <Divider />
                    <h4>You have a report request pending. Please check back later.</h4>
                </>
            )}
            {!!PDFViewer && !!Report && isShowingReport ? (
                <>
                    <Divider />
                    <PDFViewer showToolbar={false} style={{ width: "100%", height: "800px" }}>
                        <Report />
                    </PDFViewer>
                </>
            ) : null}
            <Divider />
            <h4 className="card-subtitle mb-2 text-muted subheading">Account Deletion</h4>
            <p>Delete your account and remove all of your data from our system.</p>
            <Alert
                description="This action is irreversible. You will be instantly signed out of the platform."
                type="warning"
                showIcon
            />
            <Popconfirm
                placement="top"
                title="Delete your account completely?"
                okText="Yes, delete everything"
                cancelText="Cancel"
                className="confirmation"
                onConfirm={requestDeletion}
            >
                <Button
                    className="secondary danger"
                    style={{ marginTop: "20px" }}
                    loading={requestingDelete}
                    disabled={requestsActive}
                >
                    Delete data
                </Button>
            </Popconfirm>
            <TermsModal
                visible={isTermsModalVisible}
                onClose={() => setTermsModalVisible(false)}
                termsContent={termsContent}
                title={TermsTitles[currentTermsType] || "Document"}
                termsVersionId={termsQueries[currentTermsType]?.data?.termsVersionId}
                termsAlreadyAccepted={hasCurrentTermBeenAccepted}
                onAgree={handleAgreeTerms}
                isCreatingTermsAction={isCreatingTermsAction}
                showFooter={true}
            />
            <EditTermsModal
                visible={isEditorModalVisible}
                onClose={() => setEditorModalVisible(false)}
                title="Edit Terms"
                termsList={termsList}
                loading={loadingAllTerms}
            />
        </Card>
    );
};

export default AccountPrivacyTab;
